import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, AreaChart, Area,
} from "recharts";
import { ArrowsClockwise, ChartLineUp } from "@phosphor-icons/react";

const PERIODS = [
  { key: "today", label: "Hoy" },
  { key: "week", label: "Esta semana" },
  { key: "month", label: "Este mes" },
  { key: "year", label: "Este año" },
];

const TABS = [
  { key: "aseguradora", label: "Por aseguradora" },
  { key: "gabinete", label: "Por gabinete" },
  { key: "perito", label: "Por perito" },
  { key: "provincia", label: "Por provincia" },
];

export default function Produccion() {
  const [period, setPeriod] = useState("today");
  const [provincia, setProvincia] = useState("");
  const [provinciasList, setProvinciasList] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("aseguradora");

  const load = async (p = period, prov = provincia) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period: p });
      if (prov) params.set("provincia", prov);
      const r = await api.get(`/dashboard/produccion?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar el dashboard");
    } finally {
      setLoading(false);
    }
  };

  // Cargar lista de provincias una vez
  useEffect(() => {
    api.get("/dashboard/produccion/provincias")
      .then((r) => setProvinciasList(r.data?.provincias || []))
      .catch(() => setProvinciasList([]));
  }, []);

  useEffect(() => { load(period, provincia); }, [period, provincia]);

  const breakdown = useMemo(() => {
    if (!data) return [];
    if (tab === "aseguradora") return data.by_aseguradora;
    if (tab === "gabinete") return data.by_gabinete;
    if (tab === "provincia") return data.by_provincia;
    return data.by_perito;
  }, [data, tab]);

  const total = data?.totals?.count || 0;
  const evo = data?.evolution_30d || [];

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl" data-testid="page-produccion">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-orange-600">Producción</div>
          <h1 className="text-2xl font-bold text-foreground">Carga de trabajo</h1>
          <p className="text-xs text-muted mt-1">
            Expedientes subidos al ERP por fecha. Los datos se regularizan en cuanto se actualiza la fecha real del encargo.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-border overflow-hidden text-xs" data-testid="period-selector">
            {PERIODS.map((p, i) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPeriod(p.key)}
                className={`px-3 py-2 font-semibold transition ${period === p.key ? "bg-orange-600 text-white" : "bg-white hover:bg-slate-50 text-foreground"} ${i > 0 ? "border-l border-border" : ""}`}
                data-testid={`period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <select
            value={provincia}
            onChange={(e) => setProvincia(e.target.value)}
            className="text-xs border border-border rounded-md bg-white px-2 py-2 hover:border-foreground focus:outline-none focus:ring-2 focus:ring-orange-300"
            data-testid="provincia-selector"
          >
            <option value="">📍 Todas las provincias</option>
            {provinciasList.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          {provincia && (
            <button
              type="button"
              onClick={() => setProvincia("")}
              className="text-[11px] text-rose-700 hover:underline"
              data-testid="clear-provincia"
            >
              ✕ limpiar
            </button>
          )}
          <button
            type="button"
            onClick={() => load(period, provincia)}
            disabled={loading}
            className="text-xs border border-border bg-white hover:border-foreground rounded-md px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="refresh-btn"
          >
            <ArrowsClockwise className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} weight="bold" />
            Actualizar
          </button>
        </div>
      </div>

      {/* KPIs principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KpiCard label={`Expedientes — ${data?.period?.label || "..."}${provincia ? ` · ${provincia}` : ""}`} value={total} hint={provincia ? `Provincia: ${provincia}` : "Subidos al ERP"} highlight />
        <KpiCard label="Aseguradoras activas" value={data?.by_aseguradora?.length || 0} hint="Distintas en el periodo" />
        <KpiCard label="Gabinetes activos" value={data?.by_gabinete?.length || 0} hint="Distintos en el periodo" />
        <KpiCard label="Provincias activas" value={data?.by_provincia?.length || 0} hint="Distintas en el periodo" />
      </div>

      {/* Layout 2 columnas en escritorio */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5">
        {/* Columna izq: gráfica evolución + lista */}
        <div className="space-y-5">
          <div className="border border-border rounded-lg bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold inline-flex items-center gap-2">
                <ChartLineUp className="w-4 h-4 text-orange-600" weight="bold" />
                Evolución últimos 30 días
              </h2>
              <span className="text-[11px] text-muted">{evo.reduce((a, b) => a + b.count, 0)} expedientes</span>
            </div>
            <div className="h-56" data-testid="evolution-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={evo} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradProd" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#E85D2C" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#E85D2C" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => d.slice(8, 10) + "/" + d.slice(5, 7)}
                    style={{ fontSize: 10 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis allowDecimals={false} style={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 6 }}
                    labelFormatter={(d) => new Date(d).toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short" })}
                    formatter={(v) => [v, "Expedientes"]}
                  />
                  <Area type="monotone" dataKey="count" stroke="#E85D2C" strokeWidth={2} fill="url(#gradProd)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

        </div>

        {/* Columna der: tabs de desglose */}
        <div className="border border-border rounded-lg bg-white">
          <div className="border-b border-border">
            <div className="inline-flex w-full">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`flex-1 px-3 py-3 text-xs font-semibold transition ${tab === t.key ? "text-orange-600 border-b-2 border-orange-600 bg-orange-50/40" : "text-muted hover:text-foreground border-b-2 border-transparent"}`}
                  data-testid={`tab-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="p-4 max-h-[700px] overflow-y-auto">
            {loading ? (
              <div className="py-10 text-center text-xs text-muted">Cargando…</div>
            ) : !breakdown?.length ? (
              <div className="py-10 text-center text-xs text-muted">Sin datos en el periodo.</div>
            ) : (
              <BreakdownList items={breakdown} total={total} tab={tab} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, hint, highlight, warning }) {
  let cls = "bg-white border-border";
  if (highlight) cls = "bg-orange-50 border-orange-200";
  if (warning && value > 0) cls = "bg-amber-50 border-amber-200";
  return (
    <div className={`border rounded-lg p-4 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted">{label}</div>
      <div className={`text-3xl font-bold tabular-nums mt-1 ${highlight ? "text-orange-700" : warning && value > 0 ? "text-amber-700" : "text-foreground"}`}>
        {value}
      </div>
      {hint && <div className="text-[10px] text-muted mt-0.5">{hint}</div>}
    </div>
  );
}

function BreakdownList({ items, total, tab }) {
  return (
    <div className="space-y-2.5">
      {items.map((it) => {
        const pct = total > 0 ? Math.round((it.count / total) * 100) : 0;
        const color = it.color || (tab === "aseguradora" ? "#E85D2C" : "#64748b");
        return (
          <div key={it.id || it.name} className="space-y-1" data-testid={`breakdown-${(it.id || it.name).toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
            <div className="flex items-baseline justify-between gap-3">
              <div className="text-xs font-medium text-foreground truncate flex items-center gap-1.5">
                {tab === "gabinete" && (
                  <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ background: `${color}22`, color }}>
                    {it.short_label}
                  </span>
                )}
                <span className="truncate">{it.name}</span>
              </div>
              <div className="text-xs tabular-nums shrink-0 flex items-baseline gap-2">
                <span className="font-bold text-foreground">{it.count}</span>
                <span className="text-[10px] text-muted">({pct}%)</span>
              </div>
            </div>
            <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
              <div
                className="h-full rounded transition-all"
                style={{ width: `${pct}%`, background: color }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
